<template>
  <v-dialog v-model="show" content-class="dialog" style="z-index: 10000">
    <div class="dialog__inner">
      <v-btn icon rounded small class="dialog__close-btn" @click="closeDialog">
        <i class="dialog__close icons" />
      </v-btn>

      <div class="dialog__header">
        <div class="dialog__title titleXs titleSmSp">【アカウントのご確認】</div>
        <div class="dialog__text bodyMd">
          <span class="bodyLgMedium">「{{ currentAccountName }}」</span>でログインしています。
          <br />
          以下のアカウントでもログインが可能です。
        </div>
      </div>

      <div class="dialog__footer">
        <Button
          v-for="organization in availableOrganizations"
          :key="organization.oid"
          class="dialog__btn buttonLg"
          :title="`${organization.name}でログイン`"
          @click="navigateToOrganization(organization.subdomain ?? '')"
        />
        <button class="dialog__btn-cancel bodyMd" @click="closeDialog">キャンセル</button>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import type { GetAccountResponse } from 'wklr-backend-sdk/models';
import { useStore } from '@/store/useStore';
import { useAuth } from '@/plugins/auth';
import { useRepositories } from '@/plugins/repositories';
import Button from '@/components/renewal/common/button.vue';
import { Organization } from '@/constants';
import 'design-system/icons.module.css';
import 'design-system/tokens';

const store = useStore();
const auth = useAuth();
const repositories = useRepositories();

const account = ref<GetAccountResponse | null>(null);
const show = ref(false);

const currentAccountName = computed(() => {
  return auth.user.profile.name;
});

const availableOrganizations = computed(() => {
  if (!account.value) {
    return [];
  }

  const currentOid = auth.user.organization.oid;
  const orgs = account.value.organizations.filter((org) => {
    return org.oid !== Organization.PERSONAL_OID && org.oid !== currentOid;
  });
  return orgs;
});

const isPersonal = computed(() => {
  // auth.user.organization には「現在アクセスしている」組織が入ってくる
  // personal ドメインにアクセスしていて、組織に属している場合に、メッセージを表示する
  return auth.user.organization.oid === Organization.PERSONAL_OID;
});

const navigateToOrganization = (subdomain: string) => {
  const destination = `https://${subdomain}.legalscape.jp`;
  window.location.href = destination;
  closeDialog();
};

const closeDialog = () => {
  show.value = false;
  store.commit('setHasClosedAccountConfirmationDialog', true);
};

const initialize = async () => {
  if (!isPersonal.value) {
    return;
  }

  account.value = await repositories.accounts.getAccount();

  if (availableOrganizations.value.length === 0) {
    return;
  }

  const hasClosedDialog = store.state.global.hasClosedAccountConfirmationDialog;
  if (!hasClosedDialog) {
    show.value = true;
  }
};

onMounted(() => {
  initialize();
});
</script>

<style lang="scss" src="./account-confirmation-dialog.scss" scoped />
